/**
 * AdminPanel — full-screen modal overlay with 3 tabs, opened via
 * AdminButton next to the theme toggle (specs/open/
 * feedback-submissions-management.md §9.1).
 *
 * Sized exactly to soda-5672's 3 nested stories, not an open-ended tab
 * system: Feedback Management (soda-5673, `feedback-submissions-
 * management.md`, implemented), Conversation ID Lookup (soda-5674,
 * `conversation-id-lookup.md`, implemented), User Analysis Report
 * (soda-5675, `user-analysis-report.md`, implemented). No router -- this
 * codebase has none anywhere (confirmed by reading App.tsx/
 * RoadmapButton.tsx/FeedbackButton.tsx before implementation), and
 * introducing one for 3 fixed tabs would be disproportionate. Tab
 * selection is local component state, not URL state.
 */
import React, { useState } from 'react';
import { ThemeColors } from './useTheme';
import { FeedbackManagement } from './FeedbackManagement';
import { ConversationLookup } from './ConversationLookup';
import { UserAnalysisReport } from './UserAnalysisReport';

interface AdminPanelProps {
  colors: ThemeColors;
  onClose: () => void;
}

type AdminTab = 'feedback' | 'conversationLookup' | 'userAnalysis';

const TABS: { id: AdminTab; label: string }[] = [
  { id: 'feedback', label: 'Feedback Management' },
  { id: 'conversationLookup', label: 'Conversation ID Lookup' },
  { id: 'userAnalysis', label: 'User Analysis Report' },
];

export function AdminPanel({ colors, onClose }: AdminPanelProps): React.ReactElement {
  const [activeTab, setActiveTab] = useState<AdminTab>('feedback');

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: colors.overlay,
    zIndex: 2000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    boxSizing: 'border-box',
  };

  const panelStyle: React.CSSProperties = {
    backgroundColor: colors.bg,
    color: colors.text,
    borderRadius: '8px',
    width: '100%',
    maxWidth: '900px',
    height: '100%',
    maxHeight: '800px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
    overflow: 'hidden',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={panelStyle} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 20px 0',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px' }}>🔧 Admin</h2>
          <button
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', color: colors.textMuted, padding: '4px' }}
            title="Close"
          >
            ✕
          </button>
        </div>

        <div style={{ display: 'flex', gap: '4px', padding: '12px 20px 0', borderBottom: `1px solid ${colors.border}` }}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                background: 'transparent',
                border: 'none',
                borderBottom: activeTab === tab.id ? `2px solid ${colors.suggestionText}` : '2px solid transparent',
                color: activeTab === tab.id ? colors.text : colors.textMuted,
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: activeTab === tab.id ? 600 : 400,
                padding: '8px 12px',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {activeTab === 'feedback' && <FeedbackManagement colors={colors} />}
          {activeTab === 'conversationLookup' && <ConversationLookup colors={colors} />}
          {activeTab === 'userAnalysis' && <UserAnalysisReport colors={colors} />}
        </div>
      </div>
    </div>
  );
}
